import { Modal } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import type { SegmentInterface, SegmentModel } from '@/types'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'

export const NULL_SEGMENT: SegmentInterface = {
  id: '',
  code: -1,
  description: 'Нет сегмента',
  owner: '',
  isForClient: false,
  isActive: false,
}

type UseSegmentEditorParams = {
  organisationId: string | undefined
  organisationName?: string
  initialSegment: SegmentInterface | null
  canEdit: boolean
  includeNullSegment?: boolean
  onChanged?: (segment: SegmentInterface) => void
}

const toSegmentInterface = (model: SegmentModel): SegmentInterface => ({
  id: model.id,
  code: model.code,
  description: model.description,
  owner: model.ownerOrganisation,
  isForClient: model.isForClient,
  isActive: model.isActive,
})

export function useSegmentEditor({
  organisationId,
  organisationName,
  initialSegment,
  canEdit,
  includeNullSegment = true,
  onChanged,
}: UseSegmentEditorParams) {
  const [segmentsDtos, setSegmentsDtos] = useState<SegmentInterface[]>([])
  const [segmentModels, setSegmentModels] = useState<SegmentModel[]>([])
  const [segment, setSegment] = useState<SegmentInterface>(
    initialSegment ?? NULL_SEGMENT
  )
  const [loadingSegments, setLoadingSegments] = useState(false)

  const segmentsLoadedRef = useRef(false)
  const pendingOriginalSegmentRef = useRef<SegmentInterface | null>(null)
  const [modal, contextHolder] = Modal.useModal()

  useEffect(() => {
    segmentsLoadedRef.current = false
    setSegmentsDtos([])
  }, [organisationId, canEdit])

  useEffect(() => {
    setSegment(initialSegment ?? NULL_SEGMENT)
  }, [initialSegment])

  useEffect(() => {
    return () => {
      if (organisationId) {
        toast.dismiss(`segment-confirm-${organisationId}`)
      }
    }
  }, [organisationId])

  const loadSegments = async () => {
    if (segmentsLoadedRef.current) return

    setLoadingSegments(true)

    try {
      segmentsLoadedRef.current = true

      const response = await api.get('/segments/mine')
      const allSegments: SegmentModel[] =
        response.data.segments || response.data
      const availableAsInterface = allSegments
        .filter((s) => s.status === 'AVAILABLE')
        .map(toSegmentInterface)

      setSegmentModels(allSegments)
      setSegmentsDtos(
        canEdit
          ? includeNullSegment
            ? [...availableAsInterface, NULL_SEGMENT]
            : availableAsInterface
          : [segment]
      )
    } catch {
      toast.error('Не удалось загрузить сегменты', {
        description: 'Попробуйте обновить страницу',
      })
      segmentsLoadedRef.current = false
    } finally {
      setLoadingSegments(false)
    }
  }

  const applyChangeSegment = async (
    selectedSegment: SegmentInterface,
    value: number,
    previousSegment: SegmentInterface
  ) => {
    if (!organisationId) return

    const loadingToast = toast.loading('Изменение сегмента...')

    try {
      await api.put(`/organisations/${organisationId}/segment`, {
        segmentCode: value,
      })

      toast.success('Сегмент организации изменен', {
        id: loadingToast,
        description: selectedSegment.description,
      })

      setSegmentsDtos((prev) => prev.filter((s) => s.code !== -1))

      onChanged?.(selectedSegment)
    } catch (error) {
      toast.dismiss(loadingToast)
      setSegment(previousSegment)
      handleApiError(error, modal)
    }
  }

  const handleChangeSegment = (value: number) => {
    if (!canEdit) {
      toast.warning('Нет прав на изменение сегмента')
      return
    }

    const selectedSegment = segmentsDtos.find((s) => s.code === value)
    if (!selectedSegment || !organisationId || value === segment.code) return

    const previousSegment = pendingOriginalSegmentRef.current ?? segment
    pendingOriginalSegmentRef.current = previousSegment
    setSegment(selectedSegment)

    toast.warning(`Сменить сегмент на «${selectedSegment.description}»?`, {
      id: `segment-confirm-${organisationId}`,
      description: organisationName
        ? `Для организации «${organisationName}»`
        : undefined,
      duration: Infinity,
      classNames: { toast: 'segment-confirm-toast' },
      action: {
        label: 'Подтвердить',
        onClick: () => {
          pendingOriginalSegmentRef.current = null
          applyChangeSegment(selectedSegment, value, previousSegment)
        },
      },
      cancel: {
        label: 'Отмена',
        onClick: () => {
          pendingOriginalSegmentRef.current = null
          setSegment(previousSegment)
        },
      },
      onDismiss: () => {
        if (pendingOriginalSegmentRef.current === null) return
        setSegment(pendingOriginalSegmentRef.current)
        pendingOriginalSegmentRef.current = null
      },
    })
  }

  return {
    segment,
    segmentsDtos,
    segmentModels,
    loadingSegments,
    loadSegments,
    handleChangeSegment,
    contextHolder,
  }
}
